/** Canonical navigation derived from the same access policy as routes. */

import type { AuthUser } from '@/types/auth.types'
import { canAccessRoute, type RouteAccessId } from '@/security/uiAccess'
import { isReturnPreparationV2Enabled } from '@/config/runtime'

export interface AppNavigationItem {
  id: string
  path: string
  icon: string
  label: string
  access: RouteAccessId
  /** Optional children for submenu groups (up to 3 levels deep). */
  children?: AppNavigationItem[]
}

/** Flat list for items that stand alone (no submenu parent). */
const topLevelItems: AppNavigationItem[] = [
  {
    id: 'client-vault',
    path: '/client-vault',
    icon: 'vault',
    label: 'My Client Vault',
    access: 'clientVault',
  },
  {
    id: 'firm-vault',
    path: '/firm-vault',
    icon: 'vault',
    label: 'Client Vaults',
    access: 'firmVault',
  },
  {
    id: 'firm-switches',
    path: '/firm-switches',
    icon: 'vault',
    label: 'Incoming Clients',
    access: 'firmSwitches',
  },
  {
    id: 'claim-vault',
    path: '/claim-vault',
    icon: 'vault',
    label: 'Claim My Vault',
    access: 'claimVault',
  },
  {
    id: 'my-tax',
    path: '/my-tax',
    icon: 'bi-house-heart',
    label: 'My Tax',
    access: 'clientPortal',
  },
  {
    id: 'my-business',
    path: '/my-business',
    icon: 'bi-buildings',
    label: 'My Business',
    access: 'businessPortal',
  },
  {
    id: 'dashboard',
    path: '/dashboard',
    icon: 'bi-speedometer2',
    label: 'Dashboard',
    access: 'dashboard',
  },
  {
    id: 'customers',
    path: '/customers',
    icon: 'bi-people',
    label: 'Customers',
    access: 'customers',
  },
  ...(isReturnPreparationV2Enabled()
    ? [
        {
          id: 'return-cases',
          path: '/return-preparation-cases',
          icon: 'bi-folder2-open',
          label: 'Return Cases (V2)',
          access: 'returnCases' as RouteAccessId,
        },
      ]
    : []),
  {
    id: 'documents',
    path: '/documents',
    icon: 'bi-file-earmark-text',
    label: 'Documents',
    access: 'documents',
  },
  {
    id: 'assistant-workbench',
    path: '/assistant-workbench',
    icon: 'bi-kanban',
    label: 'Assistant Workbench',
    access: 'assistantWorkbench',
  },
  {
    id: 'practice-governance',
    path: '/practice-governance',
    icon: 'bi-diagram-3',
    label: 'Practice Governance',
    access: 'practiceGovernance',
  },
  {
    id: 'payments',
    path: '/payments',
    icon: 'bi-cash-coin',
    label: 'Payments',
    access: 'payments',
  },
  { id: 'search', path: '/search', icon: 'bi-search', label: 'Search', access: 'search' },
  { id: 'users', path: '/users', icon: 'bi-person-badge', label: 'Team', access: 'users' },
  { id: 'sessions', path: '/sessions', icon: 'bi-display', label: 'Sessions', access: 'sessions' },
  { id: 'audit', path: '/audit-log', icon: 'bi-journal-text', label: 'Audit Log', access: 'audit' },
  {
    id: 'recycle-bin',
    path: '/recycle-bin',
    icon: 'bi-trash3',
    label: 'Recycle Bin',
    access: 'recycleBin',
  },
  { id: 'profile', path: '/profile', icon: 'bi-person-gear', label: 'Profile', access: 'profile' },
]

/** Items grouped under the "Settings & Admin" submenu. */
const settingsGroup: AppNavigationItem = {
  id: 'settings-group',
  path: '/settings',
  icon: 'bi-sliders',
  label: 'Settings & Admin',
  access: 'settings',
  children: [
    {
      id: 'settings',
      path: '/settings',
      icon: 'bi-sliders',
      label: 'Preferences',
      access: 'settings',
    },
    {
      id: 'organization',
      path: '/settings/organization',
      icon: 'bi-building',
      label: 'Organization',
      access: 'organizationSettings',
    },
    {
      id: 'security',
      path: '/settings/security',
      icon: 'bi-shield-lock',
      label: 'Security',
      access: 'securitySettings',
    },
    {
      id: 'rule-packs',
      path: '/settings/rule-packs',
      icon: 'bi-journal-check',
      label: 'Tax Rule Packs',
      access: 'rulePacks',
    },
    {
      id: 'billing',
      path: '/billing',
      icon: 'bi-credit-card',
      label: 'Billing',
      access: 'billing',
    },
  ],
}

/** The complete navigation config with submenu support. */
export const navigationConfig: AppNavigationItem[] = [...topLevelItems, settingsGroup]

/**
 * Flatten all navigation items (including children) for legacy consumers.
 * Returns only items accessible to the given user.
 */
export function buildNavigation(
  user: Pick<AuthUser, 'role' | 'permissions' | 'features' | 'authScope'> | null | undefined
): AppNavigationItem[] {
  const result: AppNavigationItem[] = []

  for (const item of navigationConfig) {
    if (item.children) {
      // Include the parent if any child is accessible
      const accessibleChildren = item.children.filter(child => canAccessRoute(user, child.access))
      if (accessibleChildren.length === 1 && accessibleChildren[0].id === 'settings') {
        // For non-admin roles with only Preferences access, show as a clean standalone item
        result.push(accessibleChildren[0])
      } else if (accessibleChildren.length > 0) {
        result.push({ ...item, children: accessibleChildren })
      }
    } else if (canAccessRoute(user, item.access)) {
      result.push(item)
    }
  }

  return result
}

/**
 * Flatten all navigation items including children for path matching.
 */
export function flattenNavigation(items: AppNavigationItem[]): AppNavigationItem[] {
  const flat: AppNavigationItem[] = []
  for (const item of items) {
    if (item.children) {
      flat.push(...item.children)
    } else {
      flat.push(item)
    }
  }
  return flat
}

/** Return the single, most-specific navigation item that owns a pathname. */
export function findActiveNavigationItem<T extends Pick<AppNavigationItem, 'path'>>(
  pathname: string,
  items: T[]
): T | undefined {
  return items
    .filter(item => pathname === item.path || pathname.startsWith(`${item.path}/`))
    .sort((left, right) => right.path.length - left.path.length)[0]
}
